import { useRef, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Play, Calendar, Coins, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/cn';

export interface PastQuest {
  id: string;
  week_iso: string;       // "2026-W18"
  title: string;          // already translated by the backend
  author: string;         // username, no leading "@"
  total_cents: number;
  video_url: string;      // youtube watch / youtu.be / embed URL
  date: string;           // ISO datetime
}

interface QuestTimelineProps {
  pastQuests: PastQuest[];
  /** Highlight the most recent (default: index 0). Pass week_iso to override. */
  currentWeek?: string;
  /** Cap the rendered list — used by the home-page teaser. */
  limit?: number;
  /** On mobile (<md), hide the YouTube embed/thumbnail and show only meta+CTA. */
  compactOnMobile?: boolean;
}

export default function QuestTimeline({
  pastQuests, currentWeek, limit, compactOnMobile = false,
}: QuestTimelineProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start 80%', 'end 20%'],
  });
  const lineHeight = useTransform(scrollYProgress, [0, 1], ['0%', '100%']);

  if (!pastQuests.length) return null;
  const items = typeof limit === 'number' ? pastQuests.slice(0, limit) : pastQuests;
  const highlightWeek = currentWeek ?? items[0]?.week_iso;

  return (
    <div ref={containerRef} className="relative w-full max-w-5xl mx-auto px-4 sm:px-6 py-12">
      {/* Track (background) */}
      <div
        aria-hidden
        className="absolute top-0 bottom-0 left-7 md:left-1/2 md:-translate-x-1/2 w-[2px] bg-white/10 rounded-full"
      />
      {/* Track (filled, animated by scroll progress) */}
      <motion.div
        aria-hidden
        style={{ height: lineHeight }}
        className="absolute top-0 left-7 md:left-1/2 md:-translate-x-1/2 w-[2px] rounded-full
                   bg-gradient-to-b from-fuchsia-500 via-violet-500 to-blue-500
                   shadow-[0_0_12px_rgba(168,85,247,0.6)]"
      />

      <ol className="relative space-y-12 md:space-y-20">
        {items.map((q, i) => (
          <TimelineRow
            key={q.id}
            q={q}
            index={i}
            isFeatured={q.week_iso === highlightWeek}
            compactOnMobile={compactOnMobile}
            i18nLabels={{
              proposedBy: t('timeline.proposed_by', { defaultValue: 'Proposta da' }),
              raised: t('timeline.raised', { defaultValue: 'raccolti' }),
              watch: t('timeline.watch', { defaultValue: 'Guarda su YouTube' }),
              currentWeek: t('timeline.current_week', { defaultValue: 'Settimana attiva' }),
              clickToPlay: t('timeline.click_to_play', { defaultValue: 'Clicca per riprodurre' }),
            }}
          />
        ))}
      </ol>
    </div>
  );
}

interface TimelineRowProps {
  q: PastQuest;
  index: number;
  isFeatured: boolean;
  compactOnMobile?: boolean;
  i18nLabels: {
    proposedBy: string;
    raised: string;
    watch: string;
    currentWeek: string;
    clickToPlay: string;
  };
}

function TimelineRow({ q, index, isFeatured, compactOnMobile = false, i18nLabels }: TimelineRowProps) {
  const eur = (q.total_cents / 100).toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  const weekNumber = parseWeekNumber(q.week_iso);
  const isLeft = index % 2 === 1; // desktop: alternate sides — odd rows go left
  const formattedDate = formatDate(q.date);

  return (
    <motion.li
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      className="relative"
    >
      {/* The node on the line */}
      <Node weekNumber={weekNumber} isFeatured={isFeatured} />

      {/* Card wrapper: positioned mobile (right of line) / desktop (alternating) */}
      <div
        className={cn(
          'pl-20 md:pl-0',
          'md:grid md:grid-cols-2 md:gap-12 md:items-center',
        )}
      >
        {/* Left column on desktop (only filled on alternating rows) */}
        <div className={cn('hidden md:block', isLeft ? 'order-1' : 'order-2')}>
          {!isLeft && <Spacer />}
          {isLeft && (
            <QuestCard
              q={q}
              eur={eur}
              isFeatured={isFeatured}
              align="right"
              formattedDate={formattedDate}
              compactOnMobile={compactOnMobile}
              i18nLabels={i18nLabels}
            />
          )}
        </div>

        {/* Right column on desktop / sole column on mobile */}
        <div className={cn(isLeft ? 'md:order-2 hidden md:block' : 'md:order-1')}>
          {!isLeft ? (
            <QuestCard
              q={q}
              eur={eur}
              isFeatured={isFeatured}
              align="left"
              formattedDate={formattedDate}
              compactOnMobile={compactOnMobile}
              i18nLabels={i18nLabels}
            />
          ) : (
            <div className="hidden md:block"><Spacer /></div>
          )}
        </div>
      </div>
    </motion.li>
  );
}

function Node({ weekNumber, isFeatured }: { weekNumber: number | null; isFeatured: boolean }) {
  return (
    <div
      aria-hidden
      className="absolute left-0 md:left-1/2 md:-translate-x-1/2 top-2 z-10
                 flex items-center justify-center"
    >
      <motion.div
        initial={{ scale: 0.6, opacity: 0 }}
        whileInView={{ scale: 1, opacity: 1 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className={cn(
          'relative w-14 h-14 rounded-full flex items-center justify-center',
          'border-2 backdrop-blur-md',
          isFeatured
            ? 'bg-violet-600/30 border-violet-300 shadow-[0_0_24px_rgba(168,85,247,0.7)]'
            : 'bg-slate-900/70 border-white/15 shadow-[0_0_8px_rgba(168,85,247,0.25)]',
        )}
      >
        {/* inner pulse on featured */}
        {isFeatured && (
          <span className="absolute inset-0 rounded-full bg-violet-500/40 animate-ping pointer-events-none" />
        )}
        <span className="relative font-press text-[10px] sm:text-xs text-white tracking-wider leading-none text-center">
          <span className="block opacity-60 text-[8px] mb-0.5">WEEK</span>
          <span className="block">{weekNumber ?? '—'}</span>
        </span>
      </motion.div>
    </div>
  );
}

function QuestCard({
  q, eur, isFeatured, align, formattedDate, compactOnMobile, i18nLabels,
}: {
  q: PastQuest;
  eur: string;
  isFeatured: boolean;
  align: 'left' | 'right';
  formattedDate: string;
  compactOnMobile?: boolean;
  i18nLabels: TimelineRowProps['i18nLabels'];
}) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.5, delay: 0.15 }}
      className={cn(
        'relative rounded-2xl overflow-hidden',
        'bg-white/[0.04] backdrop-blur-xl',
        'border border-white/10',
        'transition-shadow duration-300 hover:bg-white/[0.06] hover:border-white/15',
        isFeatured
          ? 'shadow-[0_0_30px_rgba(168,85,247,0.45)] ring-1 ring-violet-500/40'
          : 'shadow-[0_8px_32px_rgba(0,0,0,0.4)]',
        align === 'right' ? 'md:text-right' : '',
      )}
    >
      {/* current-week chip */}
      {isFeatured && (
        <div
          className={cn(
            'absolute top-3 z-10 px-2.5 py-1 rounded-md',
            'bg-violet-500/90 backdrop-blur text-[10px] font-press tracking-wider text-white uppercase',
            'shadow-[0_0_12px_rgba(168,85,247,0.6)]',
            align === 'right' ? 'left-3' : 'right-3',
          )}
        >
          {i18nLabels.currentWeek}
        </div>
      )}

      {/* Video: always on desktop. On mobile: only when not in compact mode. */}
      <div className={cn(compactOnMobile ? 'hidden md:block' : '')}>
        <LazyYouTube videoUrl={q.video_url} title={q.title} clickToPlayLabel={i18nLabels.clickToPlay} />
      </div>

      <div className="p-5 sm:p-6 space-y-3">
        {/* meta line */}
        <div
          className={cn(
            'flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-pixel uppercase tracking-wider text-white/50',
            align === 'right' ? 'md:justify-end' : '',
          )}
        >
          <span className="inline-flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {q.week_iso}
          </span>
          <span className="opacity-50">•</span>
          <span>{formattedDate}</span>
        </div>

        <h3 className="font-pixel text-lg sm:text-xl leading-snug text-white">
          {q.title}
        </h3>

        <p
          className={cn(
            'text-sm flex flex-wrap items-center gap-x-2 gap-y-1',
            align === 'right' ? 'md:justify-end' : '',
          )}
        >
          <span className="text-white/60 font-pixel">
            {i18nLabels.proposedBy}{' '}
            <span className="text-white/90">@{q.author}</span>
          </span>
          <span className="text-white/30">•</span>
          <span
            className="inline-flex items-center gap-1 font-press text-amber-300
                       drop-shadow-[0_0_8px_rgba(251,191,36,0.35)]"
          >
            <Coins className="w-3.5 h-3.5" />
            €{eur} {i18nLabels.raised}
          </span>
        </p>

        <div className={cn('pt-1', align === 'right' ? 'md:flex md:justify-end' : '')}>
          <a
            href={q.video_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-pixel uppercase tracking-wider
                       text-violet-300 hover:text-violet-200 transition-colors"
          >
            {i18nLabels.watch}
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>
    </motion.article>
  );
}

function LazyYouTube({
  videoUrl, title, clickToPlayLabel,
}: { videoUrl: string; title: string; clickToPlayLabel: string }) {
  const [playing, setPlaying] = useState(false);
  const videoId = extractYouTubeId(videoUrl);

  if (!videoId) {
    // not a youtube URL → fallback CTA
    return (
      <a
        href={videoUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="block aspect-video bg-black/60 flex items-center justify-center
                   font-pixel text-white/60 hover:text-white transition-colors"
      >
        <Play className="w-10 h-10" />
      </a>
    );
  }

  if (playing) {
    return (
      <div className="aspect-video bg-black">
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          loading="lazy"
          className="w-full h-full border-0"
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setPlaying(true)}
      aria-label={clickToPlayLabel}
      className="group relative block aspect-video w-full overflow-hidden bg-black"
    >
      <img
        src={`https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`}
        srcSet={`https://i.ytimg.com/vi/${videoId}/hqdefault.jpg 480w, https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg 1280w`}
        sizes="(max-width: 768px) 100vw, 600px"
        alt={title}
        loading="lazy"
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
        onError={(e) => {
          // Some videos lack maxresdefault; fall back to hqdefault
          const img = e.currentTarget;
          if (!img.src.endsWith('hqdefault.jpg')) {
            img.src = `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
          }
        }}
      />
      {/* gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent
                      transition-opacity duration-300 group-hover:opacity-90" />
      {/* play button */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-violet-600/95
                        flex items-center justify-center
                        shadow-[0_0_30px_rgba(168,85,247,0.7)]
                        transition-transform duration-300 group-hover:scale-110 group-active:scale-95">
          <Play className="w-7 h-7 sm:w-9 sm:h-9 text-white fill-white ml-1" />
        </div>
      </div>
    </button>
  );
}

function Spacer() {
  return <div aria-hidden className="invisible h-1" />;
}

// ----- helpers --------------------------------------------------------------

function extractYouTubeId(url: string): string | null {
  if (!url) return null;
  // youtu.be/<id>   |   youtube.com/watch?v=<id>   |   youtube.com/embed/<id>
  const re = /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([A-Za-z0-9_-]{11})/;
  const m = url.match(re);
  return m ? m[1] : null;
}

function parseWeekNumber(weekIso: string): number | null {
  // expect "2026-W18"
  const m = weekIso.match(/-W(\d{1,2})$/i);
  return m ? parseInt(m[1], 10) : null;
}

function formatDate(iso: string): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return iso.slice(0, 10);
  }
}
